/**
 * Labstep
 *
 * @module models/entity-user
 * @desc Typescript user class for EntityUser
 */

import { Type } from 'class-transformer';
import type { Device } from 'labstep-web/models/device.model';
import { Entity } from 'labstep-web/models/entity.model';
import type { EntityStateWorkflow } from 'labstep-web/models/entity-state-workflow.model';
import { EntityUserRole } from 'labstep-web/models/entity-user-role.model';
import type { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import type { Folder } from 'labstep-web/models/folder.model';
import type { OrderRequest } from 'labstep-web/models/order-request.model';
import type { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import type { PurchaseOrder } from 'labstep-web/models/purchase-order.model';
import type { Resource } from 'labstep-web/models/resource.model';
import type { ResourceItem } from 'labstep-web/models/resource-item.model';
import type { ResourceLocation } from 'labstep-web/models/resource-location.model';
import { User } from 'labstep-web/models/user.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';

export type EntityUserEntities =
  | EntityStateWorkflow
  | ExperimentWorkflow
  | ProtocolCollection
  | Resource
  | ResourceItem
  | ResourceLocation
  | OrderRequest
  | PurchaseOrder
  | Device
  | Folder;

export class EntityUser extends Entity {
  static readonly entityName = 'entity_user' as const;

  get entityName(): string {
    return EntityUser.entityName;
  }

  constructor(data: Partial<EntityUser> = {}) {
    super();
    Object.assign(this, data);
  }

  id!: number;

  created_at!: string;

  is_assigned!: boolean;

  is_contributor!: boolean;

  entity_user_role_id!: number;

  @Type(() => User)
  user!: User;

  @Type(() => EntityUserRole)
  entity_user_role!: EntityUserRole | null;

  getIsAuthor(entityAuthor: User): boolean {
    return entityAuthor ? this.user.id === entityAuthor.id : false;
  }

  getRole(entityAuthor: User): string {
    let role = '';
    if (this.is_contributor) {
      role = 'Contributor';
    }
    if (this.is_assigned) {
      role = 'Assigned';
    }
    if (this.getIsAuthor(entityAuthor)) {
      if (this.is_assigned) {
        role = 'Creator - Assigned';
      } else {
        role = `Creator`;
      }
    }

    return role;
  }

  static getHumanReadableEntityName(
    plural?: boolean,
    capitalized?: boolean,
  ): string {
    return getHumanReadableEntityName(
      this.entityName,
      plural,
      capitalized,
    );
  }
}
